/**
 * Tag chip colours (SC-376). Admins pick from these presets - hex stops of the design tokens'
 * hues (iris primary, the segment and severity hues) - or enter a custom hex. Chips render with
 * the colour as a solid fill, so the text colour is derived for contrast rather than stored.
 */
export const TAG_COLOR_PRESETS: { name: string; value: string }[] = [
  { name: 'Iris', value: '#5B47E0' },
  { name: 'Violet', value: '#7C3AED' },
  { name: 'Blue', value: '#2563EB' },
  { name: 'Cyan', value: '#0891B2' },
  { name: 'Teal', value: '#0F8A7E' },
  { name: 'Green', value: '#15803D' },
  { name: 'Amber', value: '#D97706' },
  { name: 'Orange', value: '#EA580C' },
  { name: 'Red', value: '#DC2626' },
  { name: 'Rose', value: '#BE123C' },
  { name: 'Magenta', value: '#A21CAF' },
  { name: 'Slate', value: '#64748B' },
];

export const DEFAULT_TAG_COLOR = TAG_COLOR_PRESETS[0].value;

const HEX = /^#[0-9a-f]{6}$/i;

export const isHexColor = (value: string | null | undefined): value is string => !!value && HEX.test(value.trim());

const channel = (value: number) => {
  const c = value / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};

/** WCAG relative luminance of a `#RRGGBB` colour. */
export const relativeLuminance = (hex: string): number => {
  const n = parseInt(hex.trim().slice(1), 16);
  return 0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255);
};

const DARK_TEXT = '#0F172A';
const LIGHT_TEXT = '#FFFFFF';

/** Dark or white text, whichever has the higher WCAG contrast against the fill. */
export const readableTextColor = (background: string): string => {
  if (!isHexColor(background)) return DARK_TEXT;
  const l = relativeLuminance(background);
  const contrastWithWhite = 1.05 / (l + 0.05);
  const contrastWithDark = (l + 0.05) / (relativeLuminance(DARK_TEXT) + 0.05);
  return contrastWithWhite >= contrastWithDark ? LIGHT_TEXT : DARK_TEXT;
};
